import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blog-posts';
import { allTools } from '../../mocks/tools';
import { TOOLNOVA_AUTHOR } from '../../mocks/authors';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../pages/home/components/Footer';
import { useSEO } from '../../hooks/useSEO';
import NewsletterCTA from '../../components/feature/NewsletterCTA';

const SITE_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/2a35743cdb9600edc45f9b5e09018b91.jpg';

function AuthorAvatar({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeMap = { sm: 'w-10 h-10 text-base', md: 'w-14 h-14 text-xl', lg: 'w-16 h-16 text-2xl' };
  if (TOOLNOVA_AUTHOR.photo) {
    return (
      <div className={`flex-shrink-0 rounded-full overflow-hidden bg-teal-500 ${sizeMap[size]}`}>
        <img src={TOOLNOVA_AUTHOR.photo} alt={TOOLNOVA_AUTHOR.name} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`flex-shrink-0 rounded-full flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600 font-bold text-white ${sizeMap[size]}`}>
      {TOOLNOVA_AUTHOR.name.charAt(0).toUpperCase()}
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogPosts.find(post => post.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  const mentionedTools = useMemo(() => {
    if (!article) return [];
    const plainText = article.content.replace(/<[^>]*>/g, ' ');
    return allTools
      .filter(tool => {
        const escaped = tool.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escaped}\\b`, 'i').test(plainText);
      })
      .slice(0, 6);
  }, [article]);

  // ── SEO ──────────────────────────────────────────────────────────────────
  useSEO({
    title: article ? `${article.title} — ToolNova.ai` : 'Article Not Found — ToolNova.ai',
    description: article?.description || 'The article you are looking for could not be found.',
    keywords: article ? `${article.category}, AI tools, ${article.title}` : 'blog, AI tools',
    canonical: article ? `${siteUrl}/blog/${article.slug}` : undefined,
    ogTitle: article ? `${article.title} — ToolNova.ai` : 'Article Not Found',
    ogDescription: article?.description || 'The article you are looking for could not be found.',
    ogType: 'article',
    ogUrl: article ? `${siteUrl}/blog/${article.slug}` : undefined,
    ogImage: article?.image || SITE_OG_IMAGE,
    articleAuthor: article ? 'https://toolnova.ai/about' : undefined,
    schema: article ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      image: article.image || SITE_OG_IMAGE,
      datePublished: article.date,
      dateModified: article.date,
      url: `${siteUrl}/blog/${article.slug}`,
      author: {
        '@type': 'Organization',
        name: 'ToolNova',
        url: siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'ToolNova',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://toolnova.ai/logo.png',
        },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/blog/${article.slug}` },
    } : undefined,
  });

  // ── 404 ───────────────────────────────────────────────────────────────────
  if (!article) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The article you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              &larr; Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Related posts ─────────────────────────────────────────────────────────
  const relatedByCategory = blogPosts.filter(p => p.id !== article.id && p.category === article.category).slice(0, 3);
  const fillRelated = blogPosts.filter(p => p.id !== article.id && !relatedByCategory.find(r => r.id === p.id)).slice(0, 3 - relatedByCategory.length);
  const displayRelated = [...relatedByCategory, ...fillRelated].slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />

      {/* ── Hero Header ─────────────────────────────────────────────────────── */}
      <div className="relative pt-28 pb-0">
        <div className="absolute inset-0 z-0">
          <img
            src={article.image}
            alt={`${article.title} — article cover`}
            width={1600}
            height={900}
            fetchPriority="high"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gray-950/85"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/70 to-gray-950"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-16 pt-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 group text-sm font-medium">
            <i className="ri-arrow-left-line text-base group-hover:-translate-x-1 transition-transform"></i>
            Back to Blog
          </Link>

          <div className="mb-5">
            <span className="inline-block px-4 py-1.5 bg-teal-500/15 text-teal-400 rounded-full text-xs font-semibold border border-teal-500/30 uppercase tracking-wider">
              {article.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">{article.title}</h1>
          <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-3xl">{article.description}</p>

          {/* Byline */}
          <div className="flex flex-wrap items-center gap-5 text-sm text-gray-400">
            <div className="flex items-center gap-3">
              <AuthorAvatar size="sm" />
              <div>
                <p className="text-white font-semibold text-sm">
                  By{' '}
                  <Link to="/about" className="hover:text-teal-400 transition-colors">{TOOLNOVA_AUTHOR.name}</Link>
                </p>
                <p className="text-xs text-gray-500">{TOOLNOVA_AUTHOR.title}</p>
              </div>
            </div>
            <div className="h-6 w-px bg-gray-700 hidden sm:block"></div>
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-teal-500"></i>
              <span>Published {new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="ri-time-line text-teal-500"></i>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Featured Image ────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6 mb-0">
        <div className="relative w-full h-72 md:h-96 rounded-2xl overflow-hidden border border-gray-800">
          <img
            src={article.image}
            alt={article.title}
            width={1024}
            height={576}
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/40 to-transparent"></div>
        </div>
      </div>

      {/* ── Article Body ─────────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 md:p-14 shadow-xl shadow-teal-500/5">
          <article
            className="
              prose max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:text-teal-400 prose-h2:mt-12 prose-h2:mb-5
              prose-h2:border-b prose-h2:border-teal-500/30 prose-h2:pb-3
              prose-h3:text-xl prose-h3:text-teal-300 prose-h3:mt-9 prose-h3:mb-3
              prose-p:text-white prose-p:leading-[1.95] prose-p:mb-6 prose-p:text-[1.0625rem]
              prose-strong:text-white prose-strong:font-semibold
              prose-a:text-teal-400 prose-a:no-underline hover:prose-a:text-teal-300 prose-a:font-medium
              prose-ul:text-white prose-ul:my-5 prose-ul:space-y-1
              prose-ol:text-white prose-ol:my-5
              prose-li:mb-2 prose-li:text-[1.0625rem] prose-li:leading-relaxed prose-li:text-white
              prose-blockquote:border-l-teal-500 prose-blockquote:bg-teal-500/10 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:text-white
              prose-code:text-teal-300 prose-code:bg-gray-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              [&_*]:text-white [&_h2]:text-teal-400 [&_h3]:text-teal-300 [&_a]:text-teal-400 [&_code]:text-teal-300 [&_strong]:text-white
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>

        {/* Share / Tags row */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 py-6 border-t border-gray-800">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400 mr-1">Share:</span>
            {[
              { href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`, icon: 'ri-twitter-x-line', title: 'Share on X' },
              { href: 'https://www.youtube.com/@toolnovaHQ', icon: 'ri-youtube-line', title: 'Toolnova on YouTube' },
              { href: 'https://www.instagram.com/toolnova.ai', icon: 'ri-instagram-line', title: 'Toolnova on Instagram' },
              { href: 'https://www.tiktok.com/@toolnova.ai', icon: 'ri-tiktok-line', title: 'Toolnova on TikTok' },
            ].map(s => (
              <a key={s.icon} href={s.href} target="_blank" rel="nofollow noopener noreferrer" title={s.title}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 hover:bg-teal-500/20 hover:text-teal-400 text-gray-300 transition-colors border border-gray-700 hover:border-teal-500/40 cursor-pointer">
                <i className={`${s.icon} text-sm`}></i>
              </a>
            ))}
          </div>
          <span className="inline-block px-4 py-1.5 bg-teal-500/10 text-teal-400 rounded-full text-xs font-semibold border border-teal-500/20 uppercase tracking-wider">
            {article.category}
          </span>
        </div>
      </div>

      {/* ── Browse AI Tools CTA Banner ───────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 pb-12">
        <div className="relative overflow-hidden bg-gradient-to-r from-teal-600/15 via-teal-500/10 to-teal-600/15 border border-teal-500/30 rounded-2xl p-8 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(45,212,191,0.07),transparent_70%)]"></div>
          <div className="relative z-10">
            <p className="text-lg font-bold text-white mb-2">Looking for the right AI tool?</p>
            <p className="text-gray-300 text-sm mb-6 max-w-xl mx-auto">
              Browse 150+ curated tools on ToolNova — free, no signup required.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-teal-500/25 hover:shadow-teal-400/30 hover:scale-[1.02]"
            >
              Browse All Tools
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Newsletter CTA ───────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-0 pb-4">
        <NewsletterCTA />
      </div>

      {/* ── You Might Also Like ──────────────────────────────────────────────── */}
      {displayRelated.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pb-12">
          <div className="border-t border-gray-800 pt-12">
            <h2 className="text-2xl font-bold text-white mb-8">You Might Also Like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {displayRelated.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-teal-500/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-500/5 flex flex-col cursor-pointer"
                >
                  <div className="relative w-full h-44 overflow-hidden shrink-0">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-bold text-white group-hover:text-teal-400 transition-colors line-clamp-2 leading-snug mb-3 flex-1">
                      {post.title}
                    </h3>
                    <time className="flex items-center gap-1.5 text-xs text-gray-500" dateTime={post.date}>
                      <i className="ri-calendar-line text-teal-500/70"></i>
                      {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </time>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── About the Author ─────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 pb-12">
        <div className="p-6 bg-gray-900/70 border border-gray-800/80 rounded-xl">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">About the Author</p>
          <div className="flex items-start gap-4">
            <AuthorAvatar size="md" />
            <div className="flex-1 min-w-0">
              <Link to="/about" className="text-white font-bold text-sm hover:text-teal-400 transition-colors">
                {TOOLNOVA_AUTHOR.name}
              </Link>
              <p className="text-teal-400 text-xs font-medium mt-0.5 mb-2">{TOOLNOVA_AUTHOR.title}</p>
              <p className="text-gray-400 text-xs leading-relaxed mb-3">{TOOLNOVA_AUTHOR.bio}</p>
              <div className="flex items-center gap-3">
                <Link to="/about" className="inline-flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300 transition-colors cursor-pointer">
                  More about Reece <i className="ri-arrow-right-line text-xs"></i>
                </Link>
                {TOOLNOVA_AUTHOR.twitter && (
                  <a href={TOOLNOVA_AUTHOR.twitter} target="_blank" rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-teal-400 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line"></i> @toolnovaHQ
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tools Mentioned ──────────────────────────────────────────────────── */}
      {mentionedTools.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 pb-20">
          <div className="border-t border-gray-800/70 pt-10">
            <h4 id="tools-mentioned" className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-tools-line text-teal-400"></i>
              </span>
              Tools Mentioned in This Article
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {mentionedTools.map(tool => (
                <Link
                  key={tool.id}
                  to={`/tool/${tool.slug}`}
                  className="flex items-center gap-3 p-3 bg-gray-900/70 border border-gray-800/80 rounded-xl hover:border-teal-500/40 hover:bg-gray-900 transition-all duration-200 group cursor-pointer"
                >
                  <div className="w-8 h-8 flex-shrink-0 bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden">
                    {tool.logo ? (
                      <img
                        src={tool.logo}
                        alt={`${tool.name} logo`}
                        width={20}
                        height={20}
                        loading="lazy"
                        className="w-5 h-5 object-contain"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-xs font-bold text-teal-400">{tool.name.charAt(0)}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-xs font-semibold group-hover:text-teal-400 transition-colors truncate">{tool.name}</p>
                    <p className="text-gray-500 text-xs truncate">{tool.category}</p>
                  </div>
                  <span className="ml-auto flex-shrink-0">
                    <i className="ri-arrow-right-line text-xs text-gray-600 group-hover:text-teal-400 transition-colors"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
